import classNames from "classnames";
import type { ReactNode } from "react";

interface PDFHeaderProps {
  readonly title: string;
  readonly subtitle?: string;
  readonly icon?: ReactNode;
  readonly className?: string;
  readonly titleClassName?: string;
  readonly children?: ReactNode;
}

/**
 * PDF Header Component
 * Reusable header for PDF pages with customizable title, subtitle, icon, and content
 *
 * @example
 * // Simple usage
 * <PDFHeader title="Market Map" />
 *
 * @example
 * // With subtitle
 * <PDFHeader title="Property Details" subtitle="123 Main St, City, State" />
 *
 * @example
 * // With icon and custom content
 * <PDFHeader
 *   title="Property Details"
 *   icon={<EmeraldIcon icon="stars" />}
 * >
 *   <div>Custom content</div>
 * </PDFHeader>
 */
const PDFHeader = ({
  title,
  subtitle,
  icon,
  className = "",
  titleClassName = "",
  children,
}: PDFHeaderProps) => {
  return (
    <div
      className={classNames(
        "bg-dark-secondary flex items-center py-6 px-8 pdf-grid-header",
        className,
      )}
    >
      {icon && <div className="mr-4 flex items-center">{icon}</div>}

      <div className="flex-1">
        <h1
          className={classNames(
            "text-[1.75rem] font-heading font-medium text-primary",
            titleClassName,
          )}
        >
          {title}
        </h1>
        {subtitle && <p className="text-sm text-primary mt-1">{subtitle}</p>}
      </div>

      {children && <div className="ml-auto">{children}</div>}
    </div>
  );
};

export default PDFHeader;
